import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/images/logo.png';
import KYBImage from '../../assets/svgs/kyb-start-page.svg';
import BaseImage from '../../components/base/base-image';
import AppButton from '../../components/base/button';
import { useAuthStore, useOnboardingStore } from '../../store';
import { FiLogOut } from 'react-icons/fi';
import Loader from '../../components/base/loader';
import { OnboardingKybStatusEnum } from '../../enums/onboarding.enum';

const StartKyb: React.FC = () => {
  const navigate = useNavigate();

  const { kybStatus, kybStatusDesc, getKybStatus } = useOnboardingStore();
  const { removeAuthStateFromStorage } = useAuthStore();
  const [loadingPageResource, setLoadingPageResource] = useState(true);
  const [steps, setSteps] = useState([
    {
      id: 1,
      title: 'KYB Information',
      description:
        'Enter basic details about your company, including address, ownership and business documents.',
      done: false,
    },
    {
      id: 2,
      title: 'Submitted',
      description:
        'We’ve received your details. You’ll be notified as we review them.',
      done: false,
    },
    {
      id: 3,
      title: 'Internal Review',
      description:
        'Our compliance team is reviewing your information for accuracy.',
      done: false,
      current: false,
    },
    {
      id: 4,
      title: 'Third-Party Verification',
      description:
        'Your submission is being verified by our compliance partner.',
      done: false,
      current: false,
    },
    {
      id: 5,
      title: 'Approved',
      description: "You're all set. You can now access full platform features.",
      done: false,
      current: false,
    },
  ]);

  const handleStartVerification = () => {
    navigate('/kyb-session'); // Navigate to the KYB Flow
  };

  const markStepsStatus = (statusId: number) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => {
        if (statusId === 8) {
          return step.id === 5
            ? { ...step, done: false }
            : { ...step, done: true };
        }
        return step.id <= statusId ? { ...step, done: true } : step;
      }),
    );

    if (statusId !== 8) {
      setSteps((prevSteps) =>
        prevSteps.map((step) =>
          step.id === statusId + 1 ? { ...step, current: true } : step,
        ),
      );
    }
  };

  const logout = () => {
    removeAuthStateFromStorage();
    navigate('/login');
  };

  useEffect(() => {
    const fetchKybStatus = async () => {
      setLoadingPageResource(true);
      await getKybStatus();
      if (kybStatus !== null) {
        markStepsStatus(kybStatus); // Assuming kybStatus is the ID of the last completed step
      }
      setLoadingPageResource(false);
    };

    fetchKybStatus();
  }, [kybStatus]);

  return (
    <div className="min-h-screen flex bg-gray-50 overflow-y-auto md:overflow-hidden h-screen">
      {/* Left Section */}
      <div className="w-1/2 overflow-y-scroll">
        <div className="max-w-lg mx-auto p-6">
          {/* Title */}
          <div className="flex items-center justify-between">
            <img src={Logo} alt="Logo" className="mb-4 w-24 self-start" />

            <div className="flex items-center gap-2 text-red-500 text-sm my-4 cursor-pointer">
              <FiLogOut />
              <p onClick={logout}>Logout</p>
            </div>
          </div>

          {loadingPageResource ? (
            <Loader
              width="3rem"
              borderTopColor="border-t-blue-500 mt-20 mx-auto"
            />
          ) : (
            <div>
              <h1 className="text-2xl font-bold mb-12 mt-20 text-[#111827]">
                Your KYB Status
              </h1>

              {/* Timeline */}
              <div className="relative my-12">
                {steps.map((step, index) => (
                  <div key={index}>
                    <div
                      className={`rounded-xl px-4 py-6 flex items-center gap-4 text-sm ${step.done ? 'bg-green-100 border-green-500 border' : step.current ? 'bg-gray-100' : 'bg-transparent'}`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center ${step.done ? 'bg-green-500 text-white' : 'bg-gray-300'}`}
                      >
                        {step.id}
                      </div>
                      <p>{step.title}</p>
                    </div>
                    <div className="flex items-center gap-12 pl-7">
                      {step.id < steps.length && (
                        <div
                          className={`bg-gray-200 w-[2px] ${step.current ? 'h-[6rem]' : 'h-[2rem]'}`}
                        ></div>
                      )}
                      {step.current && (
                        <p className="text-sm text-gray-600">
                          {step.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <AppButton
                label={
                  kybStatusDesc === 'KybStartedNotCompleted'
                    ? 'Continue Verification'
                    : 'Start Verification'
                }
                disabled={
                  typeof kybStatus === 'number' &&
                  kybStatus >= OnboardingKybStatusEnum.PendingVerification
                }
                clicked={handleStartVerification}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right Section */}
      <div className="hidden md:flex md:flex-col w-1/2 bg-blue-700 text-white justify-center items-center p-6 relative">
        <div className="flex gap-8 text-sm font-semibold absolute top-6 right-6">
          <p className="cursor-pointer">Products</p>
          <p className="cursor-pointer">Features</p>
          <p className="cursor-pointer">Pricing</p>
          <p className="cursor-pointer">FAQ</p>
        </div>
        <BaseImage imgSrc={KYBImage} imgAlt="KYB" imgClass="mx-auto w-4/5" />
        <div className="max-w-md text-center">
          <h1 className="text-[32px] text-white font-bold">
            Welcome to Business Verification
          </h1>
          <p className="mt-4 text-gray-200 text-sm">
            We&apos;re here to help you verify your business quickly and
            securely. This process typically takes 24–48 hours after submission.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StartKyb;
